import { supabase } from "@/integrations/supabase/client";

// Security event types
export type SecurityEventType = 'rate_limit_exceeded' | 'auth_failure' | 'suspicious_activity' | 'api_access' | 'ip_blocked' | 'data_cleanup';
export type SecuritySeverity = 'low' | 'medium' | 'high' | 'critical';

export interface SecurityEvent {
  event_type: SecurityEventType;
  ip_address?: string;
  user_agent?: string;
  endpoint?: string;
  details?: string;
  severity: SecuritySeverity;
}

/**
 * Log a security event for monitoring and alerting
 */
export const logSecurityEvent = async (event: SecurityEvent): Promise<void> => {
  try {
    // Log to database for persistent monitoring
    const { error } = await supabase
      .from('security_events')
      .insert({
        event_type: event.event_type,
        ip_address: event.ip_address,
        user_agent: event.user_agent,
        endpoint: event.endpoint,
        details: event.details,
        severity: event.severity
      });
    
    if (error) {
      console.warn('Failed to log security event to database:', error);
    }
    
    // Also call the security monitor function for real-time processing
    await supabase.functions.invoke('security-monitor', {
      body: { action: 'log', event }
    });
  } catch (error) {
    console.warn('Failed to log security event:', error);
  }
};

/**
 * Check if an action is rate limited using database function
 */
export const checkRateLimit = async (
  identifier: string, 
  actionType: string,
  limit: number = 5, 
  windowMinutes: number = 60
): Promise<{
  allowed: boolean;
  currentCount: number;
  limit: number;
  resetTime: number;
  retryAfter: number;
}> => {
  try {
    const { data, error } = await supabase.rpc('check_rate_limit', {
      p_identifier: identifier,
      p_action_type: actionType,
      p_limit: limit,
      p_window_minutes: windowMinutes
    });

    if (error) throw error;
    
    // Type assertion for the response
    const result = data as {
      allowed: boolean;
      current_count: number;
      limit: number;
      reset_time: number;
      retry_after: number;
    };
    
    return {
      allowed: result.allowed,
      currentCount: result.current_count,
      limit: result.limit,
      resetTime: result.reset_time,
      retryAfter: result.retry_after
    };
  } catch (error) {
    console.warn('Rate limit check failed:', error);
    // Fail open - allow the action if rate limiting service is down
    return {
      allowed: true,
      currentCount: 0,
      limit,
      resetTime: Date.now() / 1000 + (windowMinutes * 60),
      retryAfter: 0
    };
  }
};

/**
 * Enhanced input validation for security
 */
export const validateInput = {
  email: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254;
  },
  
  phone: (phone: string): boolean => {
    if (!phone) return true; // Optional field
    const cleanPhone = phone.replace(/\s/g, '');
    const phoneRegex = /^[\d\s\-\+\(\)]{10,15}$/;
    return phoneRegex.test(cleanPhone) && cleanPhone.length >= 10;
  },
  
  name: (name: string): boolean => {
    return typeof name === 'string' && name.length > 0 && name.length <= 100;
  },
  
  message: (message: string): boolean => {
    return typeof message === 'string' && message.trim().length >= 10 && message.length <= 5000;
  },
  
  address: (address: string): boolean => {
    return typeof address === 'string' && address.trim().length > 0 && address.length <= 200;
  },
  
  postcode: (postcode: string): boolean => {
    if (!postcode) return false;
    // UK postcode validation - basic pattern
    const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i;
    return postcodeRegex.test(postcode.trim());
  }
};

/**
 * Sanitize user input to prevent XSS and injection attacks
 */
export const sanitizeInput = {
  string: (input: string): string => {
    return input.replace(/[<>&'"]/g, '').trim();
  },
  
  html: (input: string): string => {
    return input
      .replace(/[<>&'"]/g, (match) => {
        const htmlEntities: { [key: string]: string } = {
          '<': '&lt;',
          '>': '&gt;',
          '&': '&amp;',
          '"': '&quot;',
          "'": '&#x27;'
        };
        return htmlEntities[match];
      })
      .replace(/\n/g, '<br>');
  }
};

/**
 * Get client IP address from various headers (for server-side)
 */
export const getClientIPFromRequest = (request: Request): string => {
  return request.headers.get('x-forwarded-for') || 
         request.headers.get('x-real-ip') || 
         request.headers.get('cf-connecting-ip') || 
         'unknown';
};

/**
 * Get client IP address (best effort for client-side)
 */
export const getClientIP = async (): Promise<string> => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch {
    return 'unknown';
  }
};

/**
 * Security monitoring for admin operations
 */
export const auditAdminAction = async (action: string, details?: string): Promise<void> => {
  try {
    await logSecurityEvent({
      event_type: 'api_access',
      endpoint: 'admin',
      details: `Admin action: ${action}${details ? ` - ${details}` : ''}`,
      severity: 'medium'
    });
  } catch (error) {
    console.warn('Failed to audit admin action:', error);
  }
};